import { List, Map, Record } from 'immutable';
import { FAILURE } from '../../modules/bets/actions';
import { beginOfTomorrowGMT } from '../../utils/dates/date';
import { LOAD_NEXT_SLIP, LOAD_PREVIOUS_WEEK, SET_BETS, SET_VIEW_IS_LOADING } from './actions';

const Entities = new Record({
  bets: new Map(),
  selections: new Map()
});

const SettledBet = new Record({
  betId: '',
  betPlacedTime: '',
  isoBetPlacedTime: '',
  betCashedInTime: '',
  betPotentialPayout: '',
  betPayoutFormatted: '0.00',
  betPayoutValue: 0,
  betStake: 0,
  betStakePerLine: 0,
  betType: '',
  betStatus: '',
  slipId: '',
  slipStatus: '',
  cashedOut: false,
  fundingType: '',
  selections: new List()
});

const Selection = new Record({
  selectionUniqueId: '',
  selectionId: 0,
  selectionLiveId: '',
  selectionLabel: '',
  selectionResult: 'Void',
  marketId: '',
  marketLiveId: '',
  marketName: '',
  eventId: '',
  eventName: '',
  eventStartDateTime: ''
});

const MoreChunk = new Record({
  sinceDate: '',
  ids: new List(),
  nextSlip: null
});

const mapBets = bet => [bet.betId, new SettledBet({
  betId: bet.betId,
  betPlacedTime: bet.betPlacedTime,
  betCashedInTime: bet.betCashedInTime,
  betPotentialPayout: bet.betPotentialPayout,
  betPayoutFormatted: bet.betPayoutFormatted,
  betPayoutValue: bet.betPayoutValue,
  betStake: bet.betStake,
  betStakePerLine: bet.betStakePerLine,
  betType: bet.betType,
  betStatus: bet.betStatus,
  isoBetPlacedTime: bet.isoBetPlacedTime,
  slipId: bet.slipId,
  slipStatus: bet.slipStatus,
  cashedOut: bet.betPayoutValue > 0 && bet.betStatus === 'Cashed out',
  fundingType: bet.fundingType,
  selections: new List(bet.selections.map(selection => selection.selectionUniqueId))
})];

const mapSelections = selection => [selection.selectionUniqueId, new Selection({
  selectionUniqueId: selection.selectionUniqueId,
  selectionId: selection.selectionId,
  selectionLiveId: selection.selectionLiveId,
  selectionLabel: selection.selectionLabel,
  selectionResult: (selection.selectionResult || ''),
  marketId: selection.marketId,
  marketLiveId: selection.marketLiveId,
  marketName: selection.marketName,
  eventId: selection.eventId,
  eventName: selection.eventName || `${selection.eventHome} v ${selection.eventAway}`,
  eventStartDateTime: selection.eventStartDateTime
})];

const reduceBetsToSelections = (selections, bet) => selections.concat(bet.selections);

const InitialState = new Record({
  entities: new Entities(),
  toBePaidOutIds: new List(),
  everythingElseIds: new List(),
  moreChunks: new List(),
  viewIsLoading: false,
  loadingMoreChunks: false,
  loadingMoreSlips: false,
  sinceDate: beginOfTomorrowGMT(new Date()),
  nextSlip: null
});

const makeSelectionUniqueIds = bet => {
  const b = bet;

  b.selections = bet.selections.map(selection => {
    const sel = selection;
    sel.selectionUniqueId = b.betId + sel.selectionId;
    return sel;
  });

  return b;
};

export default (state = new InitialState(), action) => {
  const isBetToBeCollected = (
    bet => bet.fundingType !== 'PLUS'
      && bet.betPayoutValue > 0
      && bet.slipStatus !== 'paid-out'
      && bet.slipStatus !== 'cancelled'
  );

  switch (action.type) {
    case SET_BETS: {
      const {
        results: { bets, nextSlip },
        incrementalByDate,
        incrementalBySlip,
        sinceDate
      } = action;
      const settledResults = bets.filter(bet => bet.betSettled).map(makeSelectionUniqueIds);
      const toBePaidOutIds = settledResults
        .filter(isBetToBeCollected)
        .map(bet => bet.betId);
      const everythingElseIds = settledResults
        .filter(bet => !isBetToBeCollected(bet))
        .map(bet => bet.betId);
      const betsEntities = settledResults
        .map(mapBets);
      const selectionsEntities = settledResults
        .reduce(reduceBetsToSelections, [])
        .map(mapSelections);

      if (incrementalByDate) {
        return state.mergeDeep({
          entities: new Entities({
            bets: new Map(betsEntities),
            selections: new Map(selectionsEntities)
          }),
          moreChunks: state.moreChunks.push(new MoreChunk({
            sinceDate,
            ids: new List(everythingElseIds),
            nextSlip
          })),
          loadingMoreChunks: false,
          sinceDate
        });
      }

      if (incrementalBySlip) {
        return state.mergeDeep({
          entities: new Entities({
            bets: new Map(betsEntities),
            selections: new Map(selectionsEntities)
          }),
          toBePaidOutIds: state.toBePaidOutIds.concat(
            toBePaidOutIds.filter(betId => !state.toBePaidOutIds.includes(betId))
          ),
          everythingElseIds: state.everythingElseIds.concat(
            everythingElseIds.filter(betId => !state.everythingElseIds.includes(betId))
          ),
          loadingMoreSlips: false,
          nextSlip
        });
      }

      return state
        .merge({
          entities: new Entities({
            bets: new Map(betsEntities),
            selections: new Map(selectionsEntities)
          }),
          toBePaidOutIds: new List(toBePaidOutIds),
          everythingElseIds: new List(everythingElseIds),
          moreChunks: new List(),
          loadingMoreChunks: false,
          loadingMoreSlips: false,
          sinceDate,
          nextSlip
        });
    }
    case SET_VIEW_IS_LOADING: {
      return state.set('viewIsLoading', action.loading);
    }
    case LOAD_PREVIOUS_WEEK: {
      return state.set('loadingMoreChunks', true);
    }
    case LOAD_NEXT_SLIP: {
      return state.set('loadingMoreSlips', true);
    }
    case FAILURE: {
      return state.merge({
        loadingMoreChunks: false,
        loadingMoreSlips: false
      });
    }
    default: {
      return state;
    }
  }
};
